import React from "react";
import { FormSections, ProgramContainer } from "../entities/formTemplateDataType";
import SectionUtils from "./SectionUtils";
import { SummaryComponent } from "../runtimeComponent/subComponent/SummaryComponent";
import { CustomizedSummaryComponent } from "../runtimeComponent/summary/CustomizedSummaryComponent";
import { BXPSummaryComponent } from "../runtimeComponent/subComponent/bxp/BXPSummaryComponent";
import HelmetComponent from "../commonComponent/HelmetComponent";

const HtmlToReactParser = require("html-to-react").Parser;

export interface GenerateReviewHtmlOptions {
    programContainer: ProgramContainer;
    isEng: boolean;
    forDownload?: boolean;
    forceShow?: boolean;
    showReview?: boolean;
    isBXP?: boolean;
    showPrintDialog?: boolean;
    isBXPSubmit?: boolean;
    htmlToReactParserInstance?: any;
    activeStepList?: Set<number>;
    addUrlToHistory?: (index: number) => void;
    setStatus?: (index: number) => void;
    callback?: (programContainer: ProgramContainer) => void;
    renderStepperHeader?: () => React.ReactNode;
    renderSubmitAndReview?: () => React.ReactNode;
    handleLoginToSaveDraft?: () => void;
    handleSaveAndContinueLater?: () => void;
    isBusy?: boolean;
    confirmationVisible?: boolean;
    summaryVisible?: boolean;
    transactionId?: string;
    submissionError?: boolean;
    showDownloadSummary?: boolean;
    applicationType?: string;
    renewType?: string;
    currentCustomizedReviewSection?: FormSections;
    parentRef?: any;
    setStateProp?: (
        confirmationVisible: boolean,
        summaryVisible: boolean,
        transactionId: string,
        submissionError: boolean,
        showDownloadSummary: boolean,
        applicationType: string,
        renewType?: string
    ) => void;
}

export const generateReviewHtml = (options: GenerateReviewHtmlOptions): React.ReactNode[] | null => {
    const {
        programContainer,
        isEng,
        forDownload = false,
        forceShow = false,
        showReview = false,
        isBXP = programContainer.CategoryId?.toUpperCase().startsWith("BXP"),
        showPrintDialog = false,
        isBXPSubmit = false,
        htmlToReactParserInstance,
        activeStepList = new Set<number>(),
        addUrlToHistory = () => undefined,
        setStatus = () => undefined,
        callback = () => undefined,
        renderStepperHeader,
        renderSubmitAndReview,
        handleLoginToSaveDraft,
        handleSaveAndContinueLater,
        isBusy = false,
        confirmationVisible = false,
        summaryVisible = false,
        transactionId = "",
        submissionError = false,
        showDownloadSummary = false,
        applicationType = "",
        renewType,
        currentCustomizedReviewSection,
        parentRef,
        setStateProp,
    } = options;

    let downloadMode = forDownload;
    const reviewHtml: React.ReactNode[] = [];

    if (!showReview && !downloadMode && !forceShow) {
        return null;
    }

    if (!downloadMode) {
        const reviewSection = currentCustomizedReviewSection
            || programContainer.ListOfFormSections?.find(p => p.CustomizedReviewPage && p.Visible);
        const tabTitle = reviewSection
            ? (isEng ? reviewSection.ENUTitle : reviewSection.FRATitle)
            : programContainer?.AppTitle?.[isEng ? "en" : "fr"];
        reviewHtml.push(
            <HelmetComponent key="review-helmet" title={tabTitle || ""} />
        );
    }

    const findIndexFromStepName = (stepName: string) => {
        const foundSection = programContainer.ListOfFormSections.find(section => section.ENUTitle == stepName);
        return foundSection ? programContainer.ListOfFormSections.indexOf(foundSection) : -1;
    };

    const handleCallbackStepIndex = (stepperIndex: number | string) => {
        const index: number = typeof stepperIndex === "string" ? findIndexFromStepName(stepperIndex) : stepperIndex;

        if (index >= 0) {
            activeStepList.add(index);
            addUrlToHistory(index);
            setStatus(index);
            callback(programContainer);
        }
    };

    if (SectionUtils.HasCustimzedSummary(programContainer)) {
        const val = programContainer.ListOfFormSections.filter(
            p => p.CustomizedReviewPage && p.Visible && !p.ExcludeFromCustomizedSummary
        )?.[0];
        const style = val?.CustomizedSummaryProps?.customizedSummaryStyle || val?.CustomizedStyle;
        
        let loginToSaveButton: React.ReactNode = null;
        if (downloadMode === false && programContainer?.CategoryId !== 'vecon') {
            const showSaveLater = programContainer?.saveLaterEnabled === true;
            if (showSaveLater) {
                if (!programContainer?.Profile && handleLoginToSaveDraft) {
                    loginToSaveButton = (
                        <div className="ontario-margin-top-24-!" key="login-to-save-draft-wrapper">
                            <button
                                key="login-to-save-draft"
                                className="ontario-button ontario-button--secondary"
                                type="button"
                                disabled={isBusy}
                                onClick={handleLoginToSaveDraft}
                            >
                                {isEng
                                    ? "Log in to save a draft"
                                    : "Ouvrir une session pour enregistrer une ébauche"}
                            </button>
                        </div>
                    );
                }
                // Show "Save and continue later" when user IS logged in
                else if (programContainer?.Profile && handleSaveAndContinueLater) {
                    loginToSaveButton = (
                        <div className="ontario-margin-top-24-!" key="save-and-continue-later-wrapper">
                            <button
                                key="save-and-continue-later"
                                className="ontario-button ontario-button--secondary"
                                type="button"
                                disabled={isBusy}
                                onClick={handleSaveAndContinueLater}
                            >
                                {isEng
                                    ? "Save and continue later"
                                    : programContainer?.CategoryId === 'vecon'
                                      ? "Enregistrer et poursuivre plus tard"
                                      : "Enregistrer et continuer plus tard"}
                            </button>
                        </div>
                    );
                }
            }
        }
        
        reviewHtml.push(
            <CustomizedSummaryComponent
                programContainer={programContainer}
                key={"custSum_" + programContainer.CategoryId}
                isEng={isEng}
                activesteps={activeStepList}
                style={style}
                callback={callback}
                callbackStepIndex={handleCallbackStepIndex}
                setStateProp={(confirmationVisible, summaryVisible, transactionId, submissionError, downloadSummary, applicationType, renewType) => {
                    if (setStateProp) {
                        setStateProp(
                            confirmationVisible,
                            summaryVisible,
                            transactionId,
                            submissionError,
                            downloadSummary,
                            applicationType,
                            renewType
                        );
                    }
                }}
                confirmationVisible={confirmationVisible}
                summaryVisible={summaryVisible}
                transactionId={transactionId}
                submissionError={submissionError}
                showDownloadSummary={showDownloadSummary}
                applicationType={applicationType}
                renewType={renewType}
                loginToSaveButton={loginToSaveButton}
                currentCustomizedReviewSection={currentCustomizedReviewSection}
            />
        );
    } else {
        if (showReview || downloadMode) {
            const htmlToReactParser = htmlToReactParserInstance ?? new HtmlToReactParser();
            if (programContainer.TopNavigation && downloadMode === false && renderStepperHeader) {
                reviewHtml.push(renderStepperHeader());
            }

            if (programContainer.CustomSummaryHeader && !isBXP) {
                const summaryHeader = isEng
                    ? programContainer.CustomSummaryHeader
                    : programContainer.CustomSummaryHeaderFRA || programContainer.CustomSummaryHeader;
                reviewHtml.push(htmlToReactParser.parse(summaryHeader));
            }

            if (programContainer.CategoryId?.toUpperCase().startsWith("BXP")) {
                downloadMode = true;
                reviewHtml.push(
                    <BXPSummaryComponent
                        key={"BXPSummaryComponent"}
                        programContainer={programContainer}
                        isEng={isEng}
                        isPrint={showPrintDialog}
                        parentRef={parentRef}
                        callback={callback}
                        callbackStepIndex={handleCallbackStepIndex}
                    />
                );
            } else {
                reviewHtml.push(
                    <SummaryComponent
                        key={"SummaryComponent"}
                        programContainer={programContainer}
                        isEng={isEng}
                    />
                );
            }
        }

        if (downloadMode === false && renderSubmitAndReview) {
            reviewHtml.push(renderSubmitAndReview());
        }
    }

    return reviewHtml;
};
